/**
 * Hook for loading and managing display names for generic reference fields
 * 
 * This hook handles:
 * 1. Loading reference entities by ID to get DisplayName
 * 2. Caching loaded entities to avoid duplicate API calls
 * 3. Tracking loading state to prevent concurrent requests
 * 4. Cleaning up when fields are cleared
 */

import { useState, useEffect, useRef } from 'react';
import { ScimEntityService } from '../services/ScimEntityService';
import { getEntityValueFromARef, createEntityRef, type EntityRef } from '../types/entity/EntityRef';

export interface UseReferenceFieldDisplayOptions {
    open: boolean;
    schemaAttributes: any[];
    formData: Record<string, any>;
    isSetTransition: boolean;
    isPolicyRule: boolean;
    isRequestApprovalType: boolean;
    entityService: ScimEntityService;
    /** When opening edit with existing entity that has expanded refs, pass them here to avoid duplicate getEntityById for each reference field. */
    initialReferenceEntities?: Record<string, any>;
    /** When in edit mode, pass the entity being edited so we skip fetching when a reference field points to the same entity (no skipReferenceExpansion=true call). */
    editingEntityId?: string | null;
    /** Entity type of the entity being edited (e.g. "employee"); used with editingEntityId to skip same-entity ref fetch. */
    editingEntityType?: string | null;
}

export type ReferenceFetchStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ReferenceFetchStatusEntry {
    status: ReferenceFetchStatus;
    error?: string;
}

export interface UseReferenceFieldDisplayResult {
    referenceEntitiesMap: Map<string, any>;
    /** Per-field status of getEntityById calls for selector display (for debug tooltips). */
    referenceFetchStatus: Record<string, ReferenceFetchStatusEntry>;
    /** Resolves display name from referenceEntitiesMap (cache) or from formData via getEntityValueFromARef. */
    getReferenceDisplayName: (formData: Record<string, unknown>, fieldRef: string | EntityRef) => string;
    updateReferenceEntity: (fieldName: string, entity: EntityRef) => void;
    clearReferenceEntity: (fieldName: string) => void;
}

export function useReferenceFieldDisplay(
    options: UseReferenceFieldDisplayOptions
): UseReferenceFieldDisplayResult {
    const {
        open,
        schemaAttributes,
        formData,
        isSetTransition,
        isPolicyRule,
        isRequestApprovalType,
        entityService,
        initialReferenceEntities,
        editingEntityId,
        editingEntityType
    } = options;

    // Map to store loaded reference entities by field name
    const [referenceEntitiesMap, setReferenceEntitiesMap] = useState<Map<string, any>>(new Map());
    /** Per-field status of getEntityById calls (for debug tooltips). */
    const [referenceFetchStatus, setReferenceFetchStatus] = useState<Record<string, ReferenceFetchStatusEntry>>({});
    // Ref to track the current map state to avoid dependency issues
    const referenceEntitiesMapRef = useRef<Map<string, any>>(new Map());
    // Ref to track which entities are currently being loaded to avoid duplicate requests
    const loadingRefsRef = useRef<Set<string>>(new Set());

    // Keep ref in sync with state
    useEffect(() => {
        referenceEntitiesMapRef.current = referenceEntitiesMap;
    }, [referenceEntitiesMap]);

    // Prime map from existing entity's expanded refs so we don't fetch with getEntityById(..., true)
    useEffect(() => {
        if (!open || !initialReferenceEntities || typeof initialReferenceEntities !== 'object') return;
        setReferenceEntitiesMap(prev => {
            const next = new Map(prev);
            for (const [name, entity] of Object.entries(initialReferenceEntities)) {
                if (entity != null && typeof entity === 'object' && (entity.Id ?? entity.id ?? entity.DisplayName)) {
                    next.set(name, entity);
                }
            }
            return next;
        });
    }, [open, initialReferenceEntities]);

    // Load display names for generic reference fields
    useEffect(() => {
        if (!open || !schemaAttributes.length) return;

        const loadReferenceDisplayNames = async () => {
            for (const attr of schemaAttributes) {
                if (!attr.multiValued && attr.isReference && !isSetTransition && !isPolicyRule && !isRequestApprovalType) {
                    const name = attr.name || attr.id || attr.value;
                    const value = formData[name];
                    const loadingKey = `${name}:${value}`;

                    if (value && typeof value === 'string' && value.trim()) {
                        // Skip attribute-picker fields: value like $.School is a schema attribute id, not an entity id (would 404 on entities API)
                        if (value.startsWith('$.')) continue;

                        // Skip PSGroup fields: PossessiveScope holds an entity type name (e.g. "Student"), not an entity id. Loading would call getEntityById("Student", "person") → invalid. Support $. prefix (e.g. $.PossessiveScope).
                        const attrNameLower = (name || '').toLowerCase().replace(/^\$\./, '');
                        if (attrNameLower === 'possessivescope' || attrNameLower === 'possessivetargetattribute' || attrNameLower === 'possessiveactorsattributes') continue;

                        const referenceEntityTypes = attr.Entities || attr.entities || [];
                        const entityTypeNames = referenceEntityTypes.map((ent: any) =>
                            typeof ent === 'string' ? ent : (ent.DisplayName || ent.Id || ent)
                        ).filter(Boolean);
                        const refEntityType = (entityTypeNames[0] || 'Person').toLowerCase();
                        // Skip when this reference points to the entity we're editing (avoid duplicate fetch; edit dialog never uses skipReferenceExpansion=true)
                        if (editingEntityId != null && editingEntityId !== '' && value === editingEntityId &&
                            editingEntityType != null && refEntityType === (editingEntityType || '').toLowerCase()) {
                            continue;
                        }

                        // In Storybook we never call the API; rely on initialReferenceEntities priming or show ID.
                        if (process.env.STORYBOOK === 'true') continue;

                        // Check if we already have this entity loaded or are currently loading it
                        const existingEntity = referenceEntitiesMapRef.current.get(name);
                        const isCurrentlyLoading = loadingRefsRef.current.has(loadingKey);

                        if ((!existingEntity || existingEntity.Id !== value) && !isCurrentlyLoading) {
                            // Mark as loading
                            loadingRefsRef.current.add(loadingKey);
                            setReferenceFetchStatus(prev => ({ ...prev, [name]: { status: 'loading' } }));

                            try {
                                const refEntity = await entityService.getEntityById(value, refEntityType, false, ['Id', 'DisplayName']);
                                setReferenceEntitiesMap(prev => {
                                    const updated = new Map(prev);
                                    updated.set(name, refEntity);
                                    return updated;
                                });
                                setReferenceFetchStatus(prev => ({ ...prev, [name]: { status: 'success' } }));
                            } catch (err) {
                                console.error(`Selector data item fetch failed for property "${name}" (entityType ${refEntityType}, id ${value}):`, err);
                                setReferenceFetchStatus(prev => ({ ...prev, [name]: { status: 'error', error: err instanceof Error ? err.message : String(err) } }));
                                // Entity not found - store just the ID
                                setReferenceEntitiesMap(prev => {
                                    const updated = new Map(prev);
                                    updated.set(name, { Id: value });
                                    return updated;
                                });
                            } finally {
                                // Remove from loading set
                                loadingRefsRef.current.delete(loadingKey);
                            }
                        }
                    } else if (!value && referenceEntitiesMapRef.current.has(name)) {
                        // Value was cleared, remove from map
                        setReferenceEntitiesMap(prev => {
                            const updated = new Map(prev);
                            updated.delete(name);
                            return updated;
                        });
                        setReferenceFetchStatus(prev => {
                            const next = { ...prev };
                            delete next[name];
                            return next;
                        });
                    }
                }
            }
        };

        loadReferenceDisplayNames();
    }, [formData, schemaAttributes, open, isSetTransition, isPolicyRule, isRequestApprovalType, entityService]);

    // Get display name for a reference field: first from referenceEntitiesMap (cache), then from formData via getEntityValueFromARef
    const getReferenceDisplayName = (formData: Record<string, unknown>, fieldRef: string | EntityRef): string => {
        const attrRef = typeof fieldRef === 'string' ? createEntityRef(fieldRef) : fieldRef;
        const mapKey = attrRef.displayName; // map is keyed by field name (e.g. "Owner")
        const loadedEntity = referenceEntitiesMap.get(mapKey) as { displayName?: string; DisplayName?: string; Name?: string; Id?: string; id?: string } | undefined;
        if (loadedEntity) {
            return (loadedEntity.displayName ?? loadedEntity.DisplayName ?? loadedEntity.Name ?? loadedEntity.Id ?? loadedEntity.id ?? '') as string;
        }
        const fieldValue = getEntityValueFromARef(attrRef, formData);
        if (fieldValue) {
            if (typeof fieldValue === 'string') {
                return fieldValue; // Will be replaced when entity loads
            }
            if (typeof fieldValue === 'object' && fieldValue !== null) {
                const obj = fieldValue as Record<string, unknown>;
                return (obj.displayName ?? obj.DisplayName ?? obj.Name ?? obj.Id ?? obj.id ?? '') as string;
            }
        }
        return '';
    };

    // Update a reference entity in the map (used when selecting a new entity)
    const updateReferenceEntity = (fieldName: string, entity: EntityRef) => {
        setReferenceEntitiesMap(prev => {
            const updated = new Map(prev);
            updated.set(fieldName, entity);
            return updated;
        });
    };

    // Clear a reference entity from the map
    const clearReferenceEntity = (fieldName: string) => {
        setReferenceEntitiesMap(prev => {
            const updated = new Map(prev);
            updated.delete(fieldName);
            return updated;
        });
    };

    return {
        referenceEntitiesMap,
        referenceFetchStatus,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity
    };
}
